import React, { lazy } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';

import { roles } from 'utility/constants/roles';
import RoleRestrictedRoute from 'utility/routing/RoleRestrictedRoute';
import HostingDetailsLayout from 'components/standard/HostingDetailsLayout';
import BeyondHostingLayout from 'components/standard/BeyondHostingLayout';
import VmWareVdcLayout from 'components/standard/VmWareVdcLayout';
import VmWareVdcSettingsLayout from 'containers/pages/servers/VmWareVdc/VmWareVdcSettingsLayout';

const Servers = lazy(() => import('containers/pages/servers/Servers'));

const ServerResize = lazy(() => import('containers/pages/servers/Resize'));

const Activity = lazy(() => import('containers/pages/servers/Activity'));

const BackupsAndImages = lazy(
	() => import('containers/pages/servers/BackupsAndImages'),
);

const Bandwidth = lazy(() => import('containers/pages/servers/Bandwidth'));

const ChangeCpanelLicence = lazy(
	() => import('containers/pages/servers/ChangeCpanelLicence'),
);

const Overview = lazy(() => import('containers/pages/servers/Overview'));

const HostingConsole = lazy(() => import('containers/pages/servers/console'));

const ScheduledTasks = lazy(
	() => import('containers/pages/servers/ScheduledTasks'),
);

const Networking = lazy(() => import('containers/pages/servers/Networking'));

const Monitoring = lazy(() => import('containers/pages/servers/Monitoring'));

const SettingsLayout = lazy(
	() => import('containers/pages/servers/Settings/SettingsLayout'),
);
const SettingsGeneral = lazy(
	() => import('containers/pages/servers/Settings/SettingsGeneral'),
);
const SettingsAddOns = lazy(
	() => import('containers/pages/servers/Settings/SettingsAddOns'),
);
const SettingsMonitoredServices = lazy(
	() => import('containers/pages/servers/Settings/SettingsMonitoredServices'),
);

const PrivateParent = lazy(
	() => import('containers/pages/servers/PrivateParent'),
);

const BeyondHostingOverview = lazy(
	() => import('containers/pages/servers/beyond-hosting/BeyondHostingOverview'),
);

const VmWareVdcOverview = lazy(
	() => import('containers/pages/servers/VmWareVdc/VmWareVdcOverview'),
);

const VmWareVdcIps = lazy(
	() => import('containers/pages/servers/VmWareVdc/VmWareVdcIps'),
);

const Images = lazy(() => import('containers/pages/servers/Images'));

const NotFound = lazy(() => import('pages/error/NotFound'));

const ServersRouter = () => {
	return (
		<React.Fragment>
			<Switch>
				<RoleRestrictedRoute
					exact
					path="/servers"
					component={Servers}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					path="/servers/images"
					component={Images}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					exact
					path="/servers/scheduled-tasks"
					component={ScheduledTasks}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					// redirecting any user trying to access the old ssh keys route to the new account level ssh keys page
					path="/servers/ssh-keys"
					component={() => <Redirect to="/account/ssh-keys" />}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					path="/servers/:uniqId/scheduled-tasks"
					component={ScheduledTasks}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					// legacy route
					path="/servers/:serverType/:uniqId/backups"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/backups`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					// legacy route
					exact
					path="/servers/:uniqId/acronis"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/backups`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					// legacy route
					path="/servers/:serverType/:uniqId/acronis"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/backups`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					// legacy route
					path="/servers/:uniqId/images"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/backups`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					// legacy route
					path="/servers/:serverType/:uniqId/images"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/backups`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					path="/servers/:uniqId/templates"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/backups`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					// legacy route
					path="/servers/:serverType/:uniqId/templates"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/backups`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					// legacy route
					path="/servers/:serverType/:uniqId/scheduled-tasks"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/scheduled-tasks`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					// legacy route
					exact
					path="/servers/:serverType/:uniqId/console"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/console`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					path="/servers/:uniqId/resize"
					component={ServerResize}
					whitelistedRoles={[roles.PURCHASER]}
				/>
				<RoleRestrictedRoute
					// legacy route
					path="/servers/:serverType/:uniqId/resize"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/resize`} />
					)}
					whitelistedRoles={[roles.PURCHASER]}
				/>
				<RoleRestrictedRoute
					path="/servers/:uniqId/cpanel"
					component={ChangeCpanelLicence}
					whitelistedRoles={[roles.PURCHASER]}
				/>
				<RoleRestrictedRoute
					// legacy route
					path="/servers/:serverType/:uniqId/cpanel"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/cpanel`} />
					)}
					whitelistedRoles={[roles.PURCHASER]}
				/>
				<RoleRestrictedRoute
					// legacy route
					path="/servers/:uniqId/reboot"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}?reboot=true`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					// legacy route
					exact
					path="/servers/:type/:uniqId/reboot"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}?reboot=true`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					// legacy route
					exact
					path="/servers/:uniqId/ips/public"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/networking`} />
					)}
					whitelistedRoles={[roles.PURCHASER]}
				/>
				<RoleRestrictedRoute
					// legacy route
					exact
					path="/servers/:serverType/:uniqId/ips/public"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/networking`} />
					)}
					whitelistedRoles={[roles.PURCHASER]}
				/>
				<RoleRestrictedRoute
					exact
					path="/servers/:uniqId/bandwidth"
					component={Bandwidth}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					// legacy route
					exact
					path="/servers/:serverType/:uniqId/bandwidth"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/bandwidth`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					exact
					path="/servers/:uniqId/support-access"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					exact
					path="/servers/:type/:uniqId/support-access"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					exact
					path="/servers/:type/:uniqId/firewall"
					component={({ match }) => (
						<Redirect to={`/servers/${match.params.uniqId}/networking`} />
					)}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					path="/servers/migration-center"
					component={() => <Redirect to="/migration-center" />}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>
				<RoleRestrictedRoute
					path="/servers/:uniqId/private-parent"
					component={PrivateParent}
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
				/>

				<Route path="/servers/:uniqId/beyond-hosting">
					<BeyondHostingLayout>
						<Switch>
							<RoleRestrictedRoute
								exact
								path="/servers/:uniqId/beyond-hosting"
								component={BeyondHostingOverview}
								whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
							/>
						</Switch>
					</BeyondHostingLayout>
				</Route>

				<Route path="/servers/:uniqId/vmware-vdc">
					<VmWareVdcLayout>
						<Switch>
							<Route path="/servers/:uniqId/vmware-vdc/settings">
								<VmWareVdcSettingsLayout>
									<Switch>
										<RoleRestrictedRoute
											exact
											path="/servers/:uniqId/vmware-vdc/settings/ips"
											component={VmWareVdcIps}
											whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
										/>
										<RoleRestrictedRoute
											exact
											path="/servers/:uniqId/vmware-vdc/settings"
											component={SettingsGeneral}
											whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
										/>
									</Switch>
								</VmWareVdcSettingsLayout>
							</Route>
							<RoleRestrictedRoute
								exact
								path="/servers/:uniqId/vmware-vdc"
								component={VmWareVdcOverview}
								whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
							/>
						</Switch>
					</VmWareVdcLayout>
				</Route>

				<Route path="/servers/:uniqId">
					<HostingDetailsLayout>
						<Switch>
							<RoleRestrictedRoute
								exact
								path="/servers/:uniqId/console"
								component={HostingConsole}
								whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
							/>
							<RoleRestrictedRoute
								exact
								path="/servers/:uniqId/activity"
								component={Activity}
								whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
							/>
							<RoleRestrictedRoute
								exact
								path="/servers/:uniqId/backups"
								component={BackupsAndImages}
								whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
							/>
							<RoleRestrictedRoute
								path="/servers/:uniqId/networking"
								component={Networking}
								whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
							/>
							<RoleRestrictedRoute
								path="/servers/:uniqId/monitoring"
								component={Monitoring}
								whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
							/>

							<Route path="/servers/:uniqId/settings">
								<SettingsLayout>
									<Switch>
										<RoleRestrictedRoute
											path="/servers/:uniqId/settings/add-ons"
											component={SettingsAddOns}
											whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
										/>

										<RoleRestrictedRoute
											path="/servers/:uniqId/settings/monitored-services"
											component={SettingsMonitoredServices}
											whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
										/>

										<RoleRestrictedRoute
											path="/servers/:uniqId/settings"
											component={SettingsGeneral}
											whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
										/>
									</Switch>
								</SettingsLayout>
							</Route>

							<RoleRestrictedRoute
								exact
								path="/servers/:uniqId"
								component={Overview}
								whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
							/>
						</Switch>
					</HostingDetailsLayout>
				</Route>

				<Route>
					<NotFound />
				</Route>
			</Switch>
		</React.Fragment>
	);
};

export default ServersRouter;
